import { ChangeDetectionStrategy, Component, computed, effect, inject, Input, signal, type SimpleChanges, untracked, ViewChild } from '@angular/core';

// App
import { fadeAnimation, fadeInAnimation } from '@23degrees/shared/animations';
import { MessageBus } from '@23degrees/shared/bus';
import { BaseClass } from '@23degrees/shared/classes/base.class';
import { BreakPointManager } from '@23degrees/shared/manager/breakpoint/breakpoint.manager';
import { ChartManager } from '@23degrees/shared/manager/chart/chart.manager';
import { MESSAGE_RECIPIENTS, REQUEST_MESSAGES, RESPONSE_MESSAGES } from '@config';
import { pointer } from 'd3-selection';

import { TTChartTypes, TypesWithLegend } from '@23degrees/shared-code/config/constants';
import { convertQuillToString, getQuillObject } from '@23degrees/shared-code/content/content-utilities';
import { NgStyle } from '@angular/common';
import { LayoutContainerComponent } from '../../layout-container/layout-container.component';
import { ToolTip2 } from '../../tooltip2/tooltip2.component';

import { Layout, LayoutChild } from '@23degrees/shared-code/domain/content/entities/valueobjects/Layout';
import { Chart, Content, StyleBackground } from '@23degrees/shared-code/models';
import { ResizeObserverDirective } from '@23degrees/shared/directives';
import { BackgroundBucketPipe, FormatPipe } from '@23degrees/shared/pipes';
import { LoadingIndicatorComponent } from '@23degrees/ui/atoms/loading-indicator/loading-indicator.component';
import { IContentSelectorOptions, ISmartConfig } from '../../index';

import { filterObject, isObject } from 'std-kit';
import { ContentDataStore } from '../../content-selector2/content-selector2.store';

// either split export more or use this one helper function directly
// and do not import whole shared code
const getLayoutChildObjects = (layout: Layout | LayoutChild) => layout.children.reduce((acc, obj) => acc.concat(obj, getLayoutChildObjects(obj)), []);

// import { hasTimeline } from '@app/utilities/object.utils';

@Component({
  selector: 'smart-chart3',
  templateUrl: './smart-chart3.component.html',
  styleUrls: ['./smart-chart3.component.scss'],
  animations: [fadeInAnimation, fadeAnimation],
  providers: [ChartManager, FormatPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [BackgroundBucketPipe, NgStyle, LayoutContainerComponent, ToolTip2, LoadingIndicatorComponent, ResizeObserverDirective],
})
export class SmartChart3Component extends BaseClass {
  private messageBus = inject(MessageBus);
  private breakPointManager = inject(BreakPointManager);
  private contentDataStore = inject(ContentDataStore);

  @Input() slug: string;
  @Input() options: IContentSelectorOptions;
  @Input() referenceID: string;

  public config = signal<ISmartConfig>({
    actions: [],
    displayScale: 1,
  });

  @ViewChild('container', { static: true }) container;

  public data: Record<string, unknown>[];
  // public content = signal<Content>(null);
  public range: number[];

  public content = this.contentDataStore.content;
  public layout = this.contentDataStore.layout;
  public chart = this.contentDataStore.chart;

  public context = computed(() => {
    return {
      layout: null, // this.layout(),
      ready: signal(false),
    };
  });

  public containerStyle: StyleBackground = {
    // backgroundColor: '#ffffff',
  };

  private prevContainerWidth: number;
  private prevContainerHeight: number;

  public displayScale = 1;

  private layoutRenderList: any = [];
  private layoutChartLoaded = false;
  private chartTypeSwitched = false; // TT to Normal and vice-versa
  private layoutChartInitialized = false; // If the layout has initialized

  private previousType: string; // Log Type changes

  private layoutElements: LayoutChild[] = [];

  constructor() {
    super();

    effect(() => {
      if (this.layout() && this.layoutChartInitialized) {
        const fromTT = TTChartTypes.includes(this.previousType) && !TTChartTypes.includes(this.content().type);
        const toTT = !TTChartTypes.includes(this.previousType) && TTChartTypes.includes(this.content().type);
        // The chart layout is init and a type-switch is on the way
        if (fromTT || toTT) {
          this.chartTypeSwitched = true;
          this.updateLayoutRenderList(this.content(), this.previousType);
        }
      }
    });

    effect(() => {
      const layout = this.layout();
      // console.log('effect layout', layout);
    });
  }

  ngOnInit() {
    this.messageBus.on(RESPONSE_MESSAGES.ContentBySlug, MESSAGE_RECIPIENTS.Chart, this.receiveContent, this.componentDestroyed$, this.referenceID);
    this.messageBus.on(RESPONSE_MESSAGES.ContentUpdate, MESSAGE_RECIPIENTS.Chart, this.receiveContentUpdate, this.componentDestroyed$, this.referenceID);
    this.messageBus.on(RESPONSE_MESSAGES.RenderData, MESSAGE_RECIPIENTS.Chart, this.receiveRenderData, this.componentDestroyed$, this.referenceID);

    this.messageBus.on(RESPONSE_MESSAGES.PreFilterData, MESSAGE_RECIPIENTS.Chart, this.receivePreFilterData, this.componentDestroyed$, this.referenceID);

    this.messageBus.on(RESPONSE_MESSAGES.DisplayScale, MESSAGE_RECIPIENTS.Chart, this.receiveDisplayScale, this.componentDestroyed$); // without referenceID because parent can be atlas/report
    this.messageBus.on('CHART_CLICK', MESSAGE_RECIPIENTS.Chart, this.receiveClick, this.componentDestroyed$, this.referenceID);
    this.messageBus.on('CHART_MOVE', MESSAGE_RECIPIENTS.Chart, this.receiveMove, this.componentDestroyed$, this.referenceID);
    this.messageBus.on('UPDATE_ACTION', MESSAGE_RECIPIENTS.Chart, this.receiveUpdateAction, this.componentDestroyed$, this.referenceID);
    this.messageBus.on(REQUEST_MESSAGES.ContentResize, MESSAGE_RECIPIENTS.Chart, this.receiveResizeChart, this.componentDestroyed$, this.referenceID);
  }

  ngOnChanges(changes: SimpleChanges) {
    // close tooltip to be sure
    this.messageBus.emit('TOOLTIP_CLOSE', [MESSAGE_RECIPIENTS.ToolTip], {}, this.referenceID);
    this.messageBus.emit(REQUEST_MESSAGES.ContentBySlug, [MESSAGE_RECIPIENTS.Chart], { slug: this.slug }, this.referenceID);

    if (changes.options?.currentValue.displayScale) {
      this.displayScale = changes.options.currentValue.displayScale;
    }
  }

  public resizeChart = (event: ResizeObserverEntry) => {
    // console.log('resizeChart');
  };

  // Data Flow
  // 1. preFilterData: before Filters are applied, raw data
  // 2. preRenderData: after Filters are applied
  // 3. Data: after Visual-Filters are applied

  private updateContent = (content: Content, preFilterData?, preRenderData?, data?, range?) => {
    if (!this.previousType) {
      this.previousType = content.type;
    }

    // this.content.localStorage.layoutActions = this.content.localStorage.layoutActions || [{ value: 'overlay', label: 'Overlay', icon: 'info-1' }];

    this.containerStyle = content.typeSpecific.style.background;

    this.previousType = content.type;

    // this.content.set(content);
    this.contentDataStore.updateContent(content);

    // console.log('[SC3] inVisualFilter', cloneObject(content.typeSpecific.inVisualFilter));

    this.config.update((prev) => ({
      ...prev,
      content: content ?? prev.content,
      data: data ?? prev.data,
      range: range ?? prev.range,
      preFilterData: preFilterData ?? prev.preFilterData,
      preRenderData: preRenderData ?? prev.preRenderData,
      referenceID: this.referenceID,
    }));

    this.messageBus.emit(REQUEST_MESSAGES.DisplayScale, [MESSAGE_RECIPIENTS.Chart], { slug: content.slug }, this.referenceID);
  };

  private receiveContent = (msg) => {
    const { content } = msg;
    this.layoutChartLoaded = false;

    this.updateLayoutRenderList(content, this.previousType);
    // console.log('[SC3] receiveContent', msg);

    // TODO why is Recipients all in there?
    const recipients = [MESSAGE_RECIPIENTS.Chart, MESSAGE_RECIPIENTS.All];
    if (this.options.editor && !content.typeSpecific.isChild) recipients.push(MESSAGE_RECIPIENTS.Editor);
    this.messageBus.emit(REQUEST_MESSAGES.RenderData, recipients, { content, dataId: content.typeSpecific.dataId }, this.referenceID);
  };

  private receiveContentUpdate = (msg, referenceID) => {
    const { content } = msg;

    // console.log('[SC3] receiveContentUpdate', msg);
    this.updateContent(content);

    if (msg.options?.requestRenderData) {
      const recipients = [MESSAGE_RECIPIENTS.Chart];
      if (this.options.editor && !content.typeSpecific.isChild) recipients.push(MESSAGE_RECIPIENTS.Editor);
      this.messageBus.emit(REQUEST_MESSAGES.RenderData, recipients, { content, dataId: content.typeSpecific.dataId }, this.referenceID);
    }
  };

  private receivePreFilterData = (msg) => {
    const { content, data } = msg;

    // console.log('[SC3] receivePreFilterData', msg);
    this.updateContent(content, data);
  };

  private receiveRenderData = (msg) => {
    const { content, data, preRenderData, range } = msg;

    // better close tooltip to be sure
    this.messageBus.emit('TOOLTIP_CLOSE', [MESSAGE_RECIPIENTS.ToolTip], {}, this.referenceID);

    // console.log('[SC3] receiveRenderData', msg);
    this.updateContent(content, this.config().preFilterData, preRenderData, data, range);
  };

  private receiveResizeChart = (msg) => {
    // console.log('[SC3] receiveResizeChart');
  };

  private receiveClick = (msg) => {
    // console.log('[SC3] receiveClick', this.referenceID, msg, this.displayScale);
    const { context, position, container, event, hover } = msg;

    const chartTop = container.parentElement.getBoundingClientRect().top;
    const containerTop = this.container.nativeElement.getBoundingClientRect().top;
    const chartOffset = chartTop - containerTop;
    const mousePos = pointer(event, this.container.nativeElement);

    const { advancedTooltip, hoverTooltip } = this.content().typeSpecific;
    const advTooltip = hover ? hoverTooltip : advancedTooltip;

    const advTT = getQuillObject(advTooltip.content);
    const ttHasText = convertQuillToString(advTooltip.content).trim().length > 0;
    const ttHasOther = advTT?.ops?.find((op) => typeof op.insert === 'object');
    if (!ttHasText && !ttHasOther) return;

    // mousePos[0] *= 1 / this.displayScale;
    // mousePos[1] *= 1 / this.displayScale;

    position.y += chartOffset * (1 / this.displayScale);

    this.messageBus.emit(
      'TOOLTIP_OPEN',
      [MESSAGE_RECIPIENTS.ToolTip],
      {
        advTooltip: advTooltip,
        context: context, // data, row, item, object, what have you
        position: position,
        container: container, // important!
        source: msg.hover ? 'mousemove' : 'click'
      },
      this.referenceID,
    );
  };

  private receiveMove = (msg) => {
    const { position, container, event } = msg;

    const chartTop = container.parentElement.getBoundingClientRect().top;
    const containerTop = this.container.nativeElement.getBoundingClientRect().top;
    const chartOffset = chartTop - containerTop;
    const mousePos = pointer(event, this.container.nativeElement);

    position.y += chartOffset * (1 / this.displayScale);

    // mousePos[0] *= 1 / this.displayScale;
    // mousePos[1] *= 1 / this.displayScale;

    this.messageBus.emit(
      'TOOLTIP_MOVE',
      [MESSAGE_RECIPIENTS.ToolTip],
      {
        position: position,
        container: container,
        source: 'mousemove'
      },
      this.content().localStorage.referenceID,
    );
  };

  private receiveDisplayScale = (msg) => {
    const { displayScale, parentSlugs } = msg;
    if (this.displayScale === displayScale) return;

    if (!this.content() || (this.content() && parentSlugs.includes(this.content().slug))) {
      this.displayScale = displayScale;

      untracked(() => {
        this.config.update((prev) => ({ ...prev, displayScale: displayScale }));
      });
    }
  };

  private receiveUpdateAction = (msg) => {
    const { enabled, action } = msg;

    let hasUpdate = false;
    let actions = this.config().actions;

    if (enabled && !actions.find((obj) => obj.value === action.value)) {
      actions.push(action);
      hasUpdate = true;
    }

    if (!enabled && actions.find((obj) => obj.value === action.value)) {
      actions = actions.filter((obj) => obj.value !== action.value);
      hasUpdate = true;
    }

    if (hasUpdate) {
      untracked(() => {
        this.content().localStorage.layoutActions = actions;
        this.config.update((prev) => ({ ...prev, actions: actions }));
      });
    }
  };

  private updateLayoutRenderList = (content: Content, previousType: string) => {
    const { layout } = content.typeSpecific;
    const activeLayout = layout.layouts[layout.active];

    const mapElements = ['search', 'map-legend', 'map-layers-legend', 'map-attributions', 'filter-annotation'];
    const elementsToIngore = [...mapElements];

    const layoutElements = filterObject(activeLayout, (obj: Layout) => isObject(obj) && Object.keys(obj).includes('type') && !obj.autoGenerated);
    this.layoutElements = layoutElements as unknown[] as LayoutChild[];

    // const headerChildren = getLayoutChildObjects(activeLayout.children[0]).filter((obj) => obj.type !== 'container');
    // const footerChildren = getLayoutChildObjects(activeLayout.children[2]).filter((obj) => obj.type !== 'container');
    // this.layoutRenderList = [...headerChildren, ...footerChildren];

    const { chart, style } = content.typeSpecific;
    if (!style.title.enabled) elementsToIngore.push('title');
    if (!style.subTitle.enabled) elementsToIngore.push('subTitle');
    if (!style.description.enabled) elementsToIngore.push('description');
    if (!chart.legend.enabled || !TypesWithLegend.includes(content.type)) elementsToIngore.push('legend');

    this.layoutElements = this.layoutElements.filter((obj) => obj.id !== 'default');
    this.layoutElements = this.layoutElements.filter((obj) => !elementsToIngore.includes(obj.type));
    // this.layoutRenderList = this.layoutRenderList.filter((obj) => !elementsToIngore.includes(obj.type));

    /*
    TODO: check if this is still needed? type switch etc should work without this

    // Vendor Charts switch are special
    // TTChartTypes -------
    const fromTT = TTChartTypes.includes(previousType) && !TTChartTypes.includes(content.type);
    const toTT = !TTChartTypes.includes(previousType) && TTChartTypes.includes(content.type);

    if (fromTT || toTT) {
      // In case of switching from/to TTCharts/Charts
      this.layoutRenderList = [];
      if (this.chartTypeSwitched) {
        setTimeout(() => {
          this.messageBus.emit('LAYOUT_READY_FOR_CHART', [MESSAGE_RECIPIENTS.Chart], {}, this.referenceID);
        });
      }
    }

    // Echarts ------------
    const fromEC = EChartTypes.includes(previousType) && !EChartTypes.includes(content.type);
    const toEC = !EChartTypes.includes(previousType) && EChartTypes.includes(content.type);

    if (fromEC || toEC) {
      // In case of switching from/to Echarts
      this.layoutRenderList = [];
      if (this.chartTypeSwitched) {
        setTimeout(() => {
          this.messageBus.emit('LAYOUT_READY_FOR_CHART', [MESSAGE_RECIPIENTS.Chart], {}, this.referenceID);
        });
      }
    }
    */
  };

  public layoutAction = (event) => {
    const { id, type, status } = event;
    const customEvents = ['legend', 'logo'];
    if ((status === 'afterNextRender' && !customEvents.includes(type)) || (customEvents.includes(type) && ['legendReady', 'logoReady'].includes(status))) {
      this.layoutElements = this.layoutElements.filter((obj) => obj.id !== id);
      // this.layoutRenderList = this.layoutRenderList.filter((obj) => obj.id !== id);
    }

    // console.log(
    //   'layoutElements',
    //   this.layoutElements.length,
    //   this.layoutElements.map((obj) => obj.type),
    //   type,
    //   status,
    // );

    if (this.layoutElements.length === 0 && !this.layoutChartLoaded) {
      this.layoutChartLoaded = true;
      this.layoutChartInitialized = true;

      // console.log('layout ready');
      this.context().ready.set(true);

      setTimeout(() => {
        this.messageBus.emit('LAYOUT_READY_FOR_CHART', [MESSAGE_RECIPIENTS.Chart], {}, this.referenceID);
      });
    }
  };

  // track by function that gives back the id so we dont rerender that often
  public getChildrenId = (idx: number, item: LayoutChild) => {
    return item.id; // or index
  };
}
